import * as React from 'react'
import styled from '@emotion/styled'

import { isEmpty } from '@livechat/data-utils'

import ActionCompletionStep from './ActionCompletionStep'
import EmptyDataInfoBanner from './EmptyDataInfoBanner'
import GTMConfigurationForm from './GTMConfigurationForm'
import { LoaderOuter, Title } from './sharedStyles'
import CircularLoader from 'components/CircularLoader'
import { useModal } from 'components/Modal'
import Redirect from 'components/Redirect'
import { useGTMAccounts } from 'hooks/useGTMAccounts'
import { useGTMContainers } from 'hooks/useGTMContainers'
import { useGTMTags } from 'hooks/useGTMTags'
import { useGTMWorkspaceStatus } from 'hooks/useGTMWorkspaceStatus'
import { useGTMWorkspaces } from 'hooks/useGTMWorkspaces'
import useTemplateId from 'hooks/useTemplateId'
import { useToast } from 'hooks/useToast'
import { useUser } from 'hooks/useUser'
import { createTag, getGTMToken, publishOWTag } from 'lib/gtm'

const StyledTitle = styled(Title)<{ isDataLoading: boolean }>`
	margin-bottom: ${({ isDataLoading }) => (isDataLoading ? '0' : '40px')};
`

const ConfigurationStep = () => {
	const showToast = useToast()
	const { organizationId } = useUser()

	const [selectedAccountId, setSelectedAccountId] = React.useState<string | null>(null)
	const [selectedContainerId, setSelectedContainerId] = React.useState<string | null>(null)
	const [selectedWorkspaceId, setSelectedWorkspaceId] = React.useState<string | null>(null)
	const [hasAlreadyInstalledOW, setHasAlreadyInstalledOW] = React.useState(false)
	const [hasDirtyWorkspace, setHasDirtyWorkspace] = React.useState(false)
	const [selectedAction, setSelectedAction] = React.useState<'add to workspace' | 'publish' | null>(null)
	const [isSaving, setIsSaving] = React.useState(false)
	const [selectedContainerName, setSelectedContainerName] = React.useState<string | null>(null)

	const { accounts } = useGTMAccounts()
	const { containers } = useGTMContainers(selectedAccountId)
	const { workspaces } = useGTMWorkspaces(selectedAccountId, selectedContainerId)
	const { tags } = useGTMTags(selectedAccountId, selectedContainerId, selectedWorkspaceId)
	const { workspaceStatus } = useGTMWorkspaceStatus(selectedAccountId, selectedContainerId, selectedWorkspaceId)
	const { templateId } = useTemplateId()

	const isDataLoaded =
		(accounts && isEmpty(accounts)) ||
		(accounts && containers && isEmpty(containers)) ||
		(accounts && containers && workspaces && tags && workspaceStatus)

	const onChangeAccount = (accountId: string) => {
		setSelectedContainerId(null)
		setSelectedWorkspaceId(null)
		setHasAlreadyInstalledOW(false)
		setHasDirtyWorkspace(false)

		setSelectedAccountId(accountId)
	}

	const onChangeContainer = (containerId: string) => {
		setHasAlreadyInstalledOW(false)
		setHasDirtyWorkspace(false)
		setSelectedWorkspaceId(null)

		setSelectedContainerId(containerId)
	}

	const handleAddToWorkspace = async () => {
		if (!selectedWorkspaceId || !selectedContainerId || !selectedAccountId) {
			return
		}

		setIsSaving(true)
		setSelectedAction('add to workspace')

		try {
			await createTag(selectedAccountId, selectedContainerId, selectedWorkspaceId, organizationId, templateId)
		} catch (error) {
			if (error instanceof Error && error.message.includes('Unauthorized')) {
				setSelectedAction(null)
			} else {
				showToast('error')
				setSelectedAction(null)
			}
		} finally {
			setIsSaving(false)
			getContainerNameFromId(selectedContainerId)
		}
	}

	const handlePublish = async () => {
		if (!selectedWorkspaceId || !selectedContainerId || !selectedAccountId) {
			return
		}

		setIsSaving(true)
		setSelectedAction('publish')

		try {
			await publishOWTag(selectedAccountId, selectedContainerId, selectedWorkspaceId, organizationId, templateId)
		} catch (error) {
			if (error instanceof Error && error.message.includes('Unauthorized')) {
				showToast('error', 'Unauthorized request. Please log in again.')
				setSelectedAction(null)
			} else {
				showToast('error')
				setSelectedAction(null)
			}
		} finally {
			setIsSaving(false)
			getContainerNameFromId(selectedContainerId)
		}
	}

	const { hideModal } = useModal()

	const getContainerNameFromId = React.useCallback(
		(containerId: string) => {
			if (!containers) {
				return null
			}

			const container = containers.find((container) => container.containerId === containerId)

			if (!container) {
				return null
			}

			setSelectedContainerName(container.name)
		},
		[containers],
	)

	React.useEffect(() => {
		if (!accounts || isEmpty(accounts)) {
			return
		}

		if (!selectedAccountId) {
			const firstAccount = accounts[0]
			setSelectedAccountId(firstAccount.accountId)
		}

		if (!containers || isEmpty(containers)) {
			return
		}

		if (!selectedContainerId) {
			const firstContainer = containers[0]
			setSelectedContainerId(firstContainer.containerId)
		}

		if (!workspaces) {
			return
		}

		const defaultWorkspaceId = workspaces.find((workspace) => workspace.name === 'Default Workspace')
			?.workspaceId as string
		setSelectedWorkspaceId(defaultWorkspaceId)
	}, [accounts, containers, workspaces, selectedAccountId, selectedContainerId])

	React.useEffect(() => {
		if (workspaceStatus) {
			setHasDirtyWorkspace(Boolean(workspaceStatus.workspaceChange || workspaceStatus.mergeConflict))
		}

		if (tags) {
			setHasAlreadyInstalledOW(tags.some((tag) => tag.name === 'OpenWidget'))
		}
	}, [workspaceStatus, tags])

	const renderConfigurationStep = () => {
		if (!isDataLoaded) {
			return (
				<LoaderOuter>
					<CircularLoader size="xlarge" />
				</LoaderOuter>
			)
		}

		if (isEmpty(accounts)) {
			return <EmptyDataInfoBanner displayType="account" />
		}

		return (
			<GTMConfigurationForm
				accounts={accounts}
				containers={containers}
				handleAddToWorkspace={handleAddToWorkspace}
				handlePublish={handlePublish}
				hasAlreadyInstalledOW={hasAlreadyInstalledOW}
				hasDirtyWorkspace={hasDirtyWorkspace}
				isSaving={isSaving}
				onChangeAccount={onChangeAccount}
				onChangeContainer={onChangeContainer}
				selectedAccountId={selectedAccountId}
				selectedContainerId={selectedContainerId}
			/>
		)
	}

	if (selectedAction) {
		return (
			<ActionCompletionStep
				selectedAction={selectedAction}
				hideModal={hideModal}
				isSaving={isSaving}
				selectedAccountId={selectedAccountId}
				selectedContainerId={selectedContainerId}
				selectedWorkspaceId={selectedWorkspaceId}
				selectedContainerName={selectedContainerName}
			/>
		)
	}

	return (
		<>
			<StyledTitle isDataLoading={!isDataLoaded}>Choose where do you want to add OpenWidget tag</StyledTitle>
			{renderConfigurationStep()}
		</>
	)
}

function withGTMToken(Component: typeof ConfigurationStep) {
	return function WithGTMToken() {
		const gtmToken = getGTMToken()
		const { withTemplateId } = useTemplateId()

		if (!gtmToken) {
			return <Redirect shallow to={withTemplateId(`gtm/sign-in`)} />
		}

		return <Component />
	}
}

export default withGTMToken(ConfigurationStep)
